/**
 * Feature mapping for the Morph engine.
 * Ported from androidx.graphics.shapes.FeatureMapping.kt
 *
 * Maps corners between two polygons by proximity, enabling the Morph engine
 * to create smooth transitions between shapes with different vertex counts.
 */

import { createDoubleMapper, type DoubleMapper } from "./float-mapping";
import type { MeasuredFeature } from "./polygon-measure";

/**
 * Creates a DoubleMapper that maps progress values between two polygons
 * based on matching their convex corners by proximity.
 *
 * The algorithm:
 * 1. Extract convex corner progress values from both polygons
 * 2. Match each corner in polygon1 to the closest corner of the same type in polygon2
 * 3. Build a piecewise-linear bidirectional mapper
 *
 * @param features1 - Measured features from polygon 1
 * @param features2 - Measured features from polygon 2
 * @returns A DoubleMapper for bidirectional progress mapping
 */
export function featureMapper(
	features1: MeasuredFeature[],
	features2: MeasuredFeature[],
): DoubleMapper {
	const convex1 = features1.filter((f) => f.convex);
	const convex2 = features2.filter((f) => f.convex);

	if (convex1.length === 0 || convex2.length === 0) {
		// No convex corners to match — use identity mapping
		return createDoubleMapper([], []);
	}

	// Map each convex corner in polygon1 to the closest in polygon2
	const fromValues: number[] = [];
	const toValues: number[] = [];

	for (const f1 of convex1) {
		let bestDist = Number.MAX_VALUE;
		let bestProgress = 0;
		for (const f2 of convex2) {
			const diff = Math.abs(f1.progress - f2.progress);
			const dist = Math.min(diff, 1 - diff); // circular distance
			if (dist < bestDist) {
				bestDist = dist;
				bestProgress = f2.progress;
			}
		}
		fromValues.push(f1.progress);
		toValues.push(bestProgress);
	}

	return createDoubleMapper(fromValues, toValues);
}
